import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface NewTripRowPayload {
  date: string;
  shift: string;
  truck: string;
  timeFull: string; // ISO UTC
  timeEmpty: string; // ISO UTC
  assignedShovel: string | null;
  shovel: string;
  material: string;
  blast: string;
  blastType: string | null;
  blastRegion: string;
  grades: string;
  assignedDump: string | null;
  dump: string;
  extraLoad: number;
  computedTonnage: number;
  computedVolume: number;
  notes: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: NewTripRowPayload) => Promise<void>;
  shiftOptions: string[];
  truckOptions: string[];
  shovelOptions: string[];
  dumpOptions: string[];
  inputOutlineSx: object;
}

const AddTripsRowModal = ({
  open,
  onClose,
  onSubmit,
  shiftOptions,
  truckOptions,
  shovelOptions,
  dumpOptions,
  inputOutlineSx,
}: Props) => {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [shift, setShift] = useState<string>('');
  const [truck, setTruck] = useState<string | null>(null);
  const [timeFull, setTimeFull] = useState<Dayjs | null>(null);
  const [timeEmpty, setTimeEmpty] = useState<Dayjs | null>(null);
  const [assignedShovel, setAssignedShovel] = useState<string | null>(null);
  const [shovel, setShovel] = useState<string | null>(null);
  const [material, setMaterial] = useState<string>('');
  const [blast, setBlast] = useState<string>('');
  const [blastType, setBlastType] = useState<string>('');
  const [blastRegion, setBlastRegion] = useState<string>('');
  const [grades, setGrades] = useState<string>('');
  const [assignedDump, setAssignedDump] = useState<string | null>(null);
  const [dump, setDump] = useState<string | null>(null);
  const [extraLoad, setExtraLoad] = useState<string>('');
  const [computedTonnage, setComputedTonnage] = useState<string>('');
  const [computedVolume, setComputedVolume] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const reset = () => {
    setDate(dayjs());
    setShift('');
    setTruck(null);
    setTimeFull(null);
    setTimeEmpty(null);
    setAssignedShovel(null);
    setShovel(null);
    setMaterial('');
    setBlast('');
    setBlastType('');
    setBlastRegion('');
    setGrades('');
    setAssignedDump(null);
    setDump(null);
    setExtraLoad('');
    setComputedTonnage('');
    setComputedVolume('');
    setNotes('');
    setTouched(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const timeOrderInvalid = Boolean(
    timeFull && timeEmpty && timeEmpty.isBefore(timeFull)
  );

  const isNum = (v: string) => v.trim() !== '' && !Number.isNaN(Number(v));

  // Required: date, shift, truck, timeFull, timeEmpty, shovel, dump, material,
  // blast, blastRegion, grades, tonnage, volume, extraLoad.
  // Optional: assignedShovel, blastType, assignedDump, notes.
  const isValid =
    Boolean(date) &&
    Boolean(shift) &&
    Boolean(truck) &&
    Boolean(timeFull) &&
    Boolean(timeEmpty) &&
    !timeOrderInvalid &&
    Boolean(shovel) &&
    Boolean(dump) &&
    material.trim().length > 0 &&
    blast.trim().length > 0 &&
    blastRegion.trim().length > 0 &&
    grades.trim().length > 0 &&
    isNum(computedTonnage) &&
    isNum(computedVolume) &&
    isNum(extraLoad);

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValid || !date || !timeFull || !timeEmpty) return;
    setSubmitting(true);
    try {
      await onSubmit({
        date: date.format('YYYY-MM-DD'),
        shift,
        truck: truck ?? '',
        timeFull: timeFull.utc().toISOString(),
        timeEmpty: timeEmpty.utc().toISOString(),
        assignedShovel: assignedShovel ?? null,
        shovel: shovel ?? '',
        material: material.trim(),
        blast: blast.trim(),
        blastType: blastType.trim() || null,
        blastRegion: blastRegion.trim(),
        grades: grades.trim(),
        assignedDump: assignedDump ?? null,
        dump: dump ?? '',
        extraLoad: Number(extraLoad),
        computedTonnage: Number(computedTonnage),
        computedVolume: Number(computedVolume),
        notes: notes.trim(),
      });
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const labelSx = {
    fontWeight: 600,
    color: 'text.primary',
    mb: 0.75,
  } as const;

  const showErr = (v: boolean) => touched && v;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Add Row</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {/* Date */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Date *
            </Typography>
            <DatePicker
              value={date}
              maxDate={dayjs()}
              onChange={(v) => {
                setDate(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!date),
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Shift */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Shift *
            </Typography>
            <FormControl size="small" fullWidth sx={inputOutlineSx}>
              <Select
                value={shift}
                displayEmpty
                error={showErr(!shift)}
                onChange={(e) => {
                  setShift(e.target.value);
                }}
                renderValue={(v) =>
                  v || <span style={{ color: '#aaa' }}>Select shift</span>
                }
              >
                {shiftOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Truck */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Truck *
            </Typography>
            <Autocomplete
              size="small"
              options={truckOptions}
              value={truck}
              onChange={(_, v) => {
                setTruck(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select truck"
                  error={showErr(!truck)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Time Full */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Time Full *
            </Typography>
            <DateTimePicker
              value={timeFull}
              ampm={false}
              format="DD/MM/YY HH:mm"
              maxDateTime={timeEmpty ?? undefined}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={(v) => {
                setTimeFull(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!timeFull),
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Time Empty */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Time Empty *
            </Typography>
            <DateTimePicker
              value={timeEmpty}
              ampm={false}
              format="DD/MM/YY HH:mm"
              minDateTime={timeFull ?? undefined}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={(v) => {
                setTimeEmpty(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!timeEmpty || timeOrderInvalid),
                  helperText: timeOrderInvalid
                    ? 'Time Empty must be after Time Full'
                    : undefined,
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Shovel */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Shovel *
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={shovelOptions}
              value={shovel}
              onChange={(_, v) => {
                setShovel(v);
              }}
              onInputChange={(_, v) => {
                setShovel(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or enter shovel"
                  error={showErr(!shovel)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Dump */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Dump *
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={dumpOptions}
              value={dump}
              onChange={(_, v) => {
                setDump(v);
              }}
              onInputChange={(_, v) => {
                setDump(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or enter dump"
                  error={showErr(!dump)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Material */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Material *
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={material}
              placeholder="Enter material"
              error={showErr(material.trim().length === 0)}
              onChange={(e) => {
                setMaterial(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Blast */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Blast *
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={blast}
              placeholder="Enter blast"
              error={showErr(blast.trim().length === 0)}
              onChange={(e) => {
                setBlast(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Blast Region */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Blast Region *
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={blastRegion}
              placeholder="Enter blast region"
              error={showErr(blastRegion.trim().length === 0)}
              onChange={(e) => {
                setBlastRegion(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Grades */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Grades *
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={grades}
              placeholder="Enter grades"
              error={showErr(grades.trim().length === 0)}
              onChange={(e) => {
                setGrades(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Tonnage */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Tonnage *
            </Typography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={computedTonnage}
              placeholder="Enter tonnage"
              error={showErr(!isNum(computedTonnage))}
              onChange={(e) => {
                setComputedTonnage(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Volume */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Volume *
            </Typography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={computedVolume}
              placeholder="Enter volume"
              error={showErr(!isNum(computedVolume))}
              onChange={(e) => {
                setComputedVolume(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Extra Load */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Extra Load *
            </Typography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={extraLoad}
              placeholder="Enter extra load"
              error={showErr(!isNum(extraLoad))}
              onChange={(e) => {
                setExtraLoad(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Assigned Shovel (optional) */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Assigned Shovel
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={shovelOptions}
              value={assignedShovel}
              onChange={(_, v) => {
                setAssignedShovel(v);
              }}
              onInputChange={(_, v) => {
                setAssignedShovel(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or enter assigned shovel"
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Assigned Dump (optional) */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Assigned Dump
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={dumpOptions}
              value={assignedDump}
              onChange={(_, v) => {
                setAssignedDump(v);
              }}
              onInputChange={(_, v) => {
                setAssignedDump(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or enter assigned dump"
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Blast Type (optional) */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Blast Type
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={blastType}
              placeholder="Enter blast type"
              onChange={(e) => {
                setBlastType(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Notes (optional) — full width */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="body2" sx={labelSx}>
              Notes
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={notes}
              placeholder="Enter notes"
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting || (touched && !isValid)}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Add Row'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTripsRowModal;
